import { activityLevels, classifyWorkout, coveredMinutes, currentReading, sleepNightLabel, workoutEndMs, } from "@healthos/shared";
import { dayKeyIn, minutesIn, sameDayIn } from "./clock.js";
import { levelTimeline, levelTimelineEnd, longestActiveStretch, stepReadings, } from "./steps-window.js";
/** The body signals a read can actually be built from. */
export const RULE_BODY_KINDS = [
    "wearable.sleep",
    "wearable.stress",
    "wearable.rhr",
    "wearable.hrv",
    // Read against their own band beside rhr and hrv, so the strain read needs them fetched.
    "wearable.spo2",
    "wearable.respiration",
    "wearable.activity",
    "wearable.workout",
];
/** Everything a tick needs: the calendar, the body, and the check-in that stands in for a wearable. */
export const RULE_SIGNAL_KINDS = [
    "calendar.event",
    ...RULE_BODY_KINDS,
    "self_report.checkin",
    "environment.weather_today",
    // Carries sunset and daylight remaining on every tick, where weather_today
    // arrives once a day at best.
    "environment.solar",
    // Whether it is safe to send somebody outside, from the rows that actually
    // stay current rather than one optional field on the weather row.
    "environment.air_quality",
    "environment.pollen",
    "environment.uv",
];
function signalAt(s) {
    return new Date(s.occurredAt ?? s.createdAt ?? Number.NaN);
}
function num(v) {
    return typeof v === "number" && Number.isFinite(v) ? v : null;
}
export function minutesInZone(iso, timezone) {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : minutesIn(d, timezone);
}
function sameLocalDay(at, now, timezone) {
    return !Number.isNaN(at.getTime()) && sameDayIn(at, now, timezone);
}
/** Whether the member is out of office right now. */
export function outOfOfficeFromSignals(signals, now, timezone) {
    const dayKey = (d) => dayKeyIn(d, timezone);
    const today = dayKey(now);
    for (const s of signals) {
        if (s.kind !== "calendar.event")
            continue;
        const p = s.payload;
        if (p.eventType !== "outOfOffice")
            continue;
        const start = typeof p.startTime === "string" ? new Date(p.startTime) : null;
        const end = typeof p.endTime === "string" ? new Date(p.endTime) : null;
        if (!start || Number.isNaN(start.getTime()))
            continue;
        if (!end || Number.isNaN(end.getTime()))
            continue;
        if (p.isAllDay === true) {
            /** Compared as Google wrote them, never round-tripped through Date and Intl. */
            const startKey = String(p.startTime).slice(0, 10);
            const endKey = String(p.endTime).slice(0, 10);
            if (startKey <= today && today < endKey)
                return true;
            continue;
        }
        if (start.getTime() <= now.getTime() && now.getTime() < end.getTime())
            return true;
    }
    return false;
}
export function calendarBlocksFromSignals(signals, now, timezone, opts = {}) {
    const blocks = [];
    const seen = new Set();
    for (const s of signals) {
        if (s.kind !== "calendar.event")
            continue;
        const p = s.payload;
        const start = typeof p.startTime === "string" ? p.startTime : null;
        const end = typeof p.endTime === "string" ? p.endTime : null;
        if (!start || !end)
            continue;
        if (p.isAllDay === true || p.looksLikeMeeting === false)
            continue;
        if (!sameLocalDay(new Date(start), now, timezone))
            continue;
        const startMinutes = minutesInZone(start, timezone);
        const endMinutes = minutesInZone(end, timezone);
        if (startMinutes === null || endMinutes === null)
            continue;
        // A zero-length row is not a commitment.
        if (endMinutes === startMinutes)
            continue;
        const endToday = endMinutes < startMinutes ? MINUTES_IN_DAY : endMinutes;
        if (endToday <= startMinutes)
            continue;
        const key = typeof p.eventId === "string" && p.eventId
            ? `id:${p.eventId}`
            : `slot:${startMinutes}-${endToday}-${num(p.attendeeCount) ?? 0}-${typeof p.title === "string" ? p.title : ""}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        blocks.push({
            ...(typeof p.eventId === "string" && p.eventId ? { eventId: p.eventId } : {}),
            startMinutes,
            endMinutes: endToday,
            ...(typeof p.isOrganiser === "boolean" ? { isOrganiser: p.isOrganiser } : {}),
            ...(typeof p.recurring === "boolean" ? { recurring: p.recurring } : {}),
            attendeeCount: num(p.attendeeCount) ?? 0,
            ...(opts.readTitles && typeof p.title === "string" ? { title: p.title } : {}),
        });
    }
    return blocks.sort((a, b) => a.startMinutes - b.startMinutes);
}
const DAY_MS = 86_400_000;
/** What a working day usually costs this member, in meeting minutes. */
export function typicalMeetingMinutes(signals, timezone, minDays = 3) {
    const byDay = new Map();
    for (const s of signals) {
        if (s.kind !== "calendar.event")
            continue;
        const p = s.payload;
        if (p.isAllDay === true || p.eventType === "outOfOffice")
            continue;
        const start = typeof p.startTime === "string" ? Date.parse(p.startTime) : Number.NaN;
        const end = typeof p.endTime === "string" ? Date.parse(p.endTime) : Number.NaN;
        if (Number.isNaN(start) || Number.isNaN(end))
            continue;
        if (!(end > start && end - start < DAY_MS))
            continue;
        const key = dayKeyIn(new Date(start), timezone);
        const day = byDay.get(key) ?? [];
        day.push([start, end]);
        byDay.set(key, day);
    }
    const days = [...byDay.values()].map((d) => Math.round(coveredMinutes(d))).sort((a, b) => a - b);
    if (days.length < minDays)
        return null;
    const mid = Math.floor(days.length / 2);
    return days.length % 2 === 0
        ? Math.round((days[mid - 1] + days[mid]) / 2)
        : days[mid];
}
/** How many distinct days this member trained, over whatever history is in hand. */
/** How long this member sits before they get up, at the far end of their own range. */
const SIT_PERCENTILE = 0.95;
const SIT_FLOOR_MINUTES = 30;
const MIN_SIT_DAYS = 3;
const WAKING_FROM_HOUR = 7;
const WAKING_TO_HOUR = 21;
export function typicalSitMinutes(signals, timezone) {
    const sits = [];
    const days = new Set();
    for (const s of signals) {
        if (s.kind !== "wearable.activity")
            continue;
        const t = activityLevels(s.payload);
        if (t === null)
            continue;
        const startMs = Date.parse(t.start);
        if (Number.isNaN(startMs))
            continue;
        days.add(dayKeyIn(new Date(startMs), timezone));
        let i = 0;
        while (i < t.classes.length) {
            if (t.classes[i] !== SEDENTARY_CLASS) {
                i++;
                continue;
            }
            let j = i;
            while (j < t.classes.length && t.classes[j] === SEDENTARY_CLASS)
                j++;
            const at = new Date(startMs + i * t.minutes * 60_000);
            const hour = Number(new Intl.DateTimeFormat("en-GB", { timeZone: timezone, hour: "2-digit", hour12: false })
                .format(at)
                .slice(0, 2));
            if (hour >= WAKING_FROM_HOUR && hour < WAKING_TO_HOUR)
                sits.push((j - i) * t.minutes);
            i = j;
        }
    }
    if (days.size < MIN_SIT_DAYS || sits.length === 0)
        return null;
    sits.sort((a, b) => a - b);
    const at = sits[Math.min(sits.length - 1, Math.floor(sits.length * SIT_PERCENTILE))] ?? 0;
    return Math.max(SIT_FLOOR_MINUTES, at);
}
export function workoutDays(signals, timezone) {
    const days = new Set();
    const dayKey = (d) => dayKeyIn(d, timezone);
    for (const s of signals) {
        if (s.kind !== "wearable.workout")
            continue;
        const at = signalAt(s);
        if (Number.isNaN(at.getTime()))
            continue;
        days.add(dayKey(at));
    }
    return days.size;
}
export function calendarProviderFromSignals(signals) {
    for (const s of signals) {
        if (s.kind !== "calendar.event")
            continue;
        const src = (s.source ?? "").toLowerCase();
        if (src.includes("microsoft") || src.includes("outlook") || src.includes("graph")) {
            return "outlook";
        }
        if (src.includes("google"))
            return "google";
    }
    return "google";
}
function withinLastNight(at, now, timezone) {
    if (Number.isNaN(at.getTime()))
        return false;
    // The shared reader owns what night a reading belongs to.
    return sleepNightLabel(at, now, timezone).isLastNight;
}
/** The floor when last night never synced. Their own wake is used when it did. */
const WAKING_MINUTE = 6 * 60;
/** Past this, a day with no steps in it is the story rather than a lie-in. */
const NO_STEPS_IS_THE_STORY_MINUTE = 11 * 60;
/** And on a day off, later: a lie-in is the point of a Saturday. */
const NO_STEPS_IS_THE_STORY_MINUTE_OFF = 13 * 60;
/** When this member actually got up, from last night's own reading. */
function wokeAt(signals, now, timezone) {
    for (const s of signals) {
        if (s.kind !== "wearable.sleep" || s.payload.isNap === true)
            continue;
        const at = signalAt(s);
        if (Number.isNaN(at.getTime()) || !withinLastNight(at, now, timezone))
            continue;
        const onset = typeof s.payload.onset === "string" ? Date.parse(s.payload.onset) : Number.NaN;
        const mins = num(s.payload.durationMinutes) ?? num(s.payload.totalSleepMinutes);
        const hrs = num(s.payload.hours) ?? (mins !== null ? mins / 60 : null);
        if (Number.isNaN(onset) || hrs === null)
            continue;
        const woke = new Date(onset + hrs * 3_600_000);
        if (woke <= now)
            return woke;
    }
    return null;
}
/** The floor on what a sit may be read from: */
function notBeforeWaking(at, now, timezone, woke) {
    if (woke !== null)
        return at.getTime() < woke.getTime() ? woke : at;
    const nowMinute = minutesInZone(now.toISOString(), timezone);
    if (nowMinute === null)
        return at;
    const waking = new Date(now.getTime() - (nowMinute - WAKING_MINUTE) * 60_000);
    return at.getTime() < waking.getTime() ? waking : at;
}
/** The instant today's quiet hours ended, or null when the member set none. */
function dayStartAt(now, timezone, dayStartMinutes) {
    if (dayStartMinutes == null)
        return null;
    const nowMinute = minutesInZone(now.toISOString(), timezone);
    if (nowMinute === null || nowMinute < dayStartMinutes)
        return null;
    return new Date(now.getTime() - (nowMinute - dayStartMinutes) * 60_000);
}
/** Saturday or Sunday in the member's own zone. */
function isDayOff(now, timezone) {
    const day = new Intl.DateTimeFormat("en-GB", { timeZone: timezone, weekday: "short" }).format(now);
    return day === "Sat" || day === "Sun";
}
/** Oura's class for waking stillness. Rest and non-wear are not it. */
const SEDENTARY_CLASS = "2";
/** The ring in a drawer. Not the member moving. */
const NON_WEAR_CLASS = "0";
/** A slot the device never reported. The timeline cannot be read across it. */
const GAP_CLASS = "-";
/** Older coverage cannot answer what the member is doing now. */
const MOVEMENT_RECENCY_MS = 15 * 60_000;
/** Small count changes can be device noise rather than a walk in progress. */
const MOVEMENT_STEP_DELTA = 150;
function classAt(timeline, at) {
    if (timeline === null || timeline.classes.length === 0)
        return null;
    const startMs = Date.parse(timeline.start);
    const stepMs = timeline.minutes * 60_000;
    if (Number.isNaN(startMs) || stepMs <= 0)
        return null;
    const index = Math.min(timeline.classes.length - 1, Math.floor((at.getTime() - startMs) / stepMs));
    return index < 0 ? null : (timeline.classes[index] ?? null);
}
function movementFromLevels(timeline, now) {
    const end = levelTimelineEnd(timeline);
    if (end === null) {
        return { state: "unknown", coveredUntil: null, blocksStepStillness: false };
    }
    const coveredUntil = Math.min(now.getTime(), end);
    if (now.getTime() - coveredUntil > MOVEMENT_RECENCY_MS) {
        return { state: "unknown", coveredUntil, blocksStepStillness: false };
    }
    const to = new Date(coveredUntil);
    const latestClass = classAt(timeline, to);
    if (latestClass === NON_WEAR_CLASS || latestClass === GAP_CLASS || latestClass === null) {
        return {
            state: "unknown",
            coveredUntil,
            blocksStepStillness: latestClass === NON_WEAR_CLASS || latestClass === GAP_CLASS,
        };
    }
    const activeMinutes = longestActiveStretch(timeline, new Date(coveredUntil - MOVEMENT_RECENCY_MS), to) ?? 0;
    if (activeMinutes > 0) {
        return { state: "moving", coveredUntil, blocksStepStillness: false };
    }
    return {
        state: latestClass === SEDENTARY_CLASS ? "still" : "unknown",
        coveredUntil,
        blocksStepStillness: false,
    };
}
function movementFromSteps(activity, now) {
    const current = activity.filter((sample) => sample.at <= now);
    const latest = current.at(-1);
    if (!latest)
        return { state: "unknown", coveredUntil: null };
    const coveredUntil = latest.at.getTime();
    if (now.getTime() - coveredUntil > MOVEMENT_RECENCY_MS) {
        return { state: "unknown", coveredUntil };
    }
    // A fresh delayed sync still describes the interval ending at that sample.
    // Anchor its comparison window to device coverage, not the wall clock.
    const windowStart = coveredUntil - MOVEMENT_RECENCY_MS;
    const candidates = current.filter((sample) => sample.at.getTime() < coveredUntil &&
        sample.at.getTime() >= windowStart - MOVEMENT_RECENCY_MS);
    // Prefer a baseline around the start of the quarter-hour window. If the
    // provider syncs less often, an earlier sample inside the window can still
    // prove movement or an unchanged count without guessing across stale data.
    const before = candidates.filter((sample) => sample.at.getTime() <= windowStart).at(-1) ?? candidates.at(0);
    if (!before)
        return { state: "unknown", coveredUntil };
    const delta = latest.count - before.count;
    if (delta >= MOVEMENT_STEP_DELTA)
        return { state: "moving", coveredUntil };
    return { state: delta === 0 ? "still" : "unknown", coveredUntil };
}
/** Where the current sit began, read from the device's own activity classes. */
function sitStartFromLevels(timeline, now) {
    if (timeline === null)
        return undefined;
    const startMs = Date.parse(timeline.start);
    const stepMs = timeline.minutes * 60_000;
    if (Number.isNaN(startMs) || stepMs <= 0 || timeline.classes.length === 0)
        return undefined;
    const elapsed = Math.floor((now.getTime() - startMs) / stepMs);
    const last = Math.min(timeline.classes.length - 1, elapsed);
    if (last < 0)
        return undefined;
    const lastMs = startMs + last * stepMs;
    const lastClass = timeline.classes[last];
    if (lastClass === NON_WEAR_CLASS || lastClass === GAP_CLASS)
        return undefined;
    let first = last;
    while (first >= 0 && timeline.classes[first] === SEDENTARY_CLASS)
        first--;
    if (first >= 0 && timeline.classes[first] === GAP_CLASS)
        return undefined;
    return first === last ? new Date(lastMs + stepMs) : new Date(startMs + (first + 1) * stepMs);
}
const MINUTES_IN_DAY = 24 * 60;
// `ratio` is how far off their own usual counts, in the direction that means a
// harder day. Loose on purpose: these are nudge inputs, not diagnoses.
const STRAIN_READS = [
    { kind: "wearable.rhr", fields: ["value", "bpm"], worseWhen: "above", ratio: 1.08 },
    { kind: "wearable.hrv", fields: ["rmssd_ms", "value"], worseWhen: "below", ratio: 0.9 },
    { kind: "wearable.spo2", fields: ["value", "percent"], worseWhen: "below", ratio: 0.97 },
    {
        kind: "wearable.respiration",
        fields: ["value", "breathsPerMin"],
        worseWhen: "above",
        ratio: 1.1,
    },
];
/** Which side of usual is the worse one, for each reading `readingsOffUsual` can name. */
export const STRAIN_VERDICT = Object.fromEntries(STRAIN_READS.map((r) => [r.kind, r.worseWhen === "above" ? "over" : "under"]));
/** What a reading off its own usual contributes to the day's depletion. */
const STRAIN_WEIGHT = 0.6;
/** Which overnight readings are sitting off this member's own usual. */
function strainFromBaselines(signals, baselines, now, timezone) {
    const off = [];
    for (const read of STRAIN_READS) {
        const usual = baselines.find((b) => b.kind === read.kind)?.average ?? null;
        if (usual === null || usual <= 0)
            continue;
        const row = currentReading(signals, read.kind, now, timezone);
        if (!row)
            continue;
        let latest = null;
        for (const field of read.fields) {
            latest = num(row.payload[field]);
            if (latest !== null)
                break;
        }
        if (latest === null)
            continue;
        const bad = read.worseWhen === "above" ? latest >= usual * read.ratio : latest <= usual * read.ratio;
        if (!bad)
            continue;
        off.push(read.kind);
    }
    return off;
}
export function bodyReadFromSignals(signals, baselines, now, timezone, limits, 
/** When the member's quiet hours end, so a sit cannot begin inside them. */
dayStartMinutes) {
    let sleepHours = null;
    // Through the shared reader, which knows how long each kind stays current:
    const stressRow = currentReading(signals, "wearable.stress", now, timezone);
    const latestStress = stressRow
        ? (num(stressRow.payload.value) ?? num(stressRow.payload.score))
        : null;
    let reportedStrain = null;
    let reportedSleepPoor = false;
    let lastMovementAt = null;
    let workoutAt = null;
    let workoutToday = null;
    let stepsToday = 0;
    let sawSteps = false;
    const activity = [];
    for (const s of signals) {
        const at = signalAt(s);
        const p = s.payload;
        if (s.kind === "wearable.sleep" && withinLastNight(at, now, timezone) && p.isNap !== true) {
            const mins = num(p.durationMinutes) ?? num(p.totalSleepMinutes);
            const hrs = num(p.hours) ?? (mins !== null ? mins / 60 : null);
            if (hrs !== null && (sleepHours === null || hrs > sleepHours))
                sleepHours = hrs;
        }
        if (s.kind === "self_report.checkin" && sameLocalDay(at, now, timezone)) {
            if (p.category === "sleep") {
                const quality = typeof p.quality === "string" ? p.quality : null;
                if (quality === "poor" || quality === "restless")
                    reportedSleepPoor = true;
            }
            else if (reportedStrain === null) {
                const parts = [];
                const mood = num(p.mood);
                const energy = num(p.energy);
                const stress = num(p.stress);
                if (mood !== null)
                    parts.push((5 - mood) / 4);
                if (energy !== null)
                    parts.push((5 - energy) / 4);
                if (stress !== null)
                    parts.push((stress - 1) / 4);
                if (parts.length > 0) {
                    reportedStrain = parts.reduce((a, b) => a + b, 0) / parts.length;
                }
            }
        }
        if (s.kind === "wearable.workout" && at <= now && sameLocalDay(at, now, timezone)) {
            const end = new Date(Math.min(workoutEndMs({ kind: s.kind, payload: p, occurredAt: at }), now.getTime()));
            if (workoutAt === null || end > workoutAt) {
                workoutAt = end;
                const { label, minutes } = classifyWorkout(p);
                workoutToday = { label, minutes };
            }
        }
        if (s.kind === "wearable.activity") {
            const count = num(p.steps);
            if (count !== null && sameLocalDay(at, now, timezone)) {
                sawSteps = true;
                stepsToday = Math.max(stepsToday, count);
            }
        }
    }
    for (const r of stepReadings(signals)) {
        if (sameLocalDay(r.at, now, timezone))
            activity.push({ at: r.at, count: r.steps });
    }
    const timeline = levelTimeline(signals, now);
    const levelMovement = movementFromLevels(timeline, now);
    const stepMovement = movementFromSteps(activity, now);
    const movementState = levelMovement.state === "moving" || stepMovement.state === "moving"
        ? "moving"
        : levelMovement.state === "still" ||
            (stepMovement.state === "still" && !levelMovement.blocksStepStillness)
            ? "still"
            : "unknown";
    // Historical sit length and current movement answer different questions.
    // Preserve the existing sit anchor while the freshness gate above decides
    // whether a notification may describe the member as still right now.
    const timelineEnd = levelTimelineEnd(timeline);
    const seenTo = timelineEnd === null
        ? now
        : new Date(Math.min(now.getTime(), Math.max(timelineEnd, activity.at(-1)?.at.getTime() ?? 0)));
    let running = 0;
    for (const sample of activity) {
        if (sample.count > running) {
            running = sample.count;
            lastMovementAt = sample.at;
        }
        stepsToday = Math.max(stepsToday, sample.count);
    }
    // A day whose count never rises is still a real sit, so it falls back to the first reading.
    const stale = currentReading(signals, "wearable.activity", now, timezone) === null;
    if (stale) {
        lastMovementAt = null;
        activity.length = 0;
        sawSteps = false;
    }
    const firstReading = activity[0]?.at ?? null;
    if (lastMovementAt === null && firstReading !== null) {
        lastMovementAt = firstReading;
    }
    // The classes beat a gap between step counts, which cannot tell a still night from a still desk.
    const readTimeline = stale ? null : timeline;
    const sitStart = sitStartFromLevels(readTimeline, now);
    if (sitStart !== undefined && (lastMovementAt === null || sitStart > lastMovementAt)) {
        lastMovementAt = sitStart;
    }
    // A session the step count cannot see is still the member moving.
    if (workoutAt !== null && (lastMovementAt === null || workoutAt > lastMovementAt)) {
        lastMovementAt = workoutAt;
    }
    // The floor bounds an anchor nothing measured, and only that.
    if (lastMovementAt !== null && sitStart === undefined) {
        lastMovementAt = notBeforeWaking(lastMovementAt, now, timezone, wokeAt(signals, now, timezone));
    }
    /** A sit cannot have begun inside the member's own quiet hours. */
    if (lastMovementAt !== null) {
        const dayStart = dayStartAt(now, timezone, dayStartMinutes);
        if (dayStart !== null && lastMovementAt.getTime() < dayStart.getTime()) {
            lastMovementAt = dayStart;
        }
    }
    /** Whether this is a member who has not got up yet rather than one sitting. */
    const nowMinute = minutesInZone(now.toISOString(), timezone);
    // A Saturday lie-in is the point of a Saturday, so the hour at which no
    // steps stops being a lie-in and starts being the story moves with the day.
    const dayOff = isDayOff(now, timezone);
    const storyMinute = dayOff ? NO_STEPS_IS_THE_STORY_MINUTE_OFF : NO_STEPS_IS_THE_STORY_MINUTE;
    const stillInBed = wokeAt(signals, now, timezone) === null &&
        stepsToday === 0 &&
        nowMinute !== null &&
        nowMinute < storyMinute;
    // Already handed to this function and previously read for resting rate only, so every step threshold downstream can be this member's own instead of a constant that fits nobody.
    const usualSteps = baselines.find((b) => b.kind === "wearable.activity")?.average ?? null;
    const strain = strainFromBaselines(signals, baselines, now, timezone);
    const restingHeartRateElevated = strain.includes("wearable.rhr");
    const parts = strain.map(() => STRAIN_WEIGHT);
    if (sleepHours !== null) {
        parts.push(Math.max(0, Math.min(1, (limits.sleepTargetHours - sleepHours) / limits.sleepTargetHours)));
    }
    if (latestStress !== null)
        parts.push(Math.max(0, Math.min(1, latestStress / 100)));
    if (reportedStrain !== null)
        parts.push(reportedStrain);
    if (reportedSleepPoor)
        parts.push(0.7);
    const depletion = parts.length > 0
        ? 0.6 * Math.max(...parts) + 0.4 * (parts.reduce((a, b) => a + b, 0) / parts.length)
        : null;
    /** Stillness is only the stillness a device recorded. */
    let idleMinutes = null;
    let sittingSinceMinutes = null;
    if (lastMovementAt !== null) {
        const measured = Math.round((seenTo.getTime() - lastMovementAt.getTime()) / 60_000);
        if (measured > 0) {
            idleMinutes = measured;
            sittingSinceMinutes = minutesInZone(lastMovementAt.toISOString(), timezone);
        }
    }
    return {
        depletion,
        usualSteps,
        /** Which overnight readings are off this member's own usual, for the card. */
        readingsOffUsual: strain,
        restingHeartRateElevated,
        // What the member said about themselves, which needs no device.
        reportedStrain,
        // No step stream at all reads as unknown, never as "sitting still".
        /** Last night, so an arm can weigh what the day is costing against it. */
        sleepHours,
        idleMinutes: sawSteps && !stillInBed ? idleMinutes : null,
        sittingSinceMinutes: sawSteps && !stillInBed ? sittingSinceMinutes : null,
        workoutToday,
        movementState,
        stepsToday: sawSteps ? stepsToday : null,
    };
}
//# sourceMappingURL=derive.js.map